package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Block.Companion.Serializer::class)
public class Block : Resource, BlockSchema {
  override val `companion`: Resource.CompanionObj<Block> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var blockedId: String
    get() = if (attributes.containsKey("blocked_id")) {
      attributes["blocked_id"] as String
    } else {
      throw NoSuchElementException("blockedId not found")
    }
    set(`value`) {
      attributes["blocked_id"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var blocked: User?
    get() = if (relationships.containsKey("blocked") && (relationships["blocked"] == null || relationships["blocked"] is User?)) {
      relationships["blocked"] as User?
    } else {
      null
    }
    set(`value`) {
      relationships["blocked"] = value
    }

  public constructor(
    blockedId: String? = null,
    userId: String? = null,
    createdAt: String? = null,
    blocked: User? = null,
  ) {
    if (blockedId != null) {
      attributes["blocked_id"] = blockedId
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (blocked != null) {
      relationships["blocked"] = blocked
    }
  }

  public companion object : Resource.CompanionObj<Block> {
    override val resourceType: String = "Block"

    override val endpoint: String = "blocks"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Block = { Block() }

    override val schema: KClass<BlockSchema> = BlockSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Block>.() -> Unit = {}): Scope<Block> = Scope(Block::class, options)

    public final suspend fun all(): CollectionProxy<Block> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Block> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Block?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Block> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Block?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Block> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Block?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Block> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Block> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Block> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Block> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Block> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Block> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Block> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Block> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Block> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Block> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Block>()
  }
}
